import { useRef } from "react"
import type { FC, ReactElement, RefObject } from "react"

import { AnimatePresence, motion } from "framer-motion"
import map from "lodash-es/map"
import { useMount } from "react-use"

import { useNotifications } from "@/3514/hooks"
import { type TNotification } from "@/3514/services"
import { idleCb } from "@/3514/utils"

import renderers from "./notification-bus.renderers"
import classes from "./notification-bus.styles"

const NotificationsBus: FC = (): ReactElement => {
    const notifications: TNotification[] = useNotifications()

    const containerRef: RefObject<HTMLElement> = useRef<HTMLElement>(null)

    useMount((): void => {
        if (containerRef.current)
            idleCb((): void => void (containerRef.current.scrollTop = containerRef.current.scrollHeight), 100)
    })

    return (
        <aside ref={containerRef} className={classes.holder()}>
            <AnimatePresence>
                {map<TNotification, ReactElement>(
                    notifications,
                    (n: TNotification, idx: number): ReactElement => (
                        <motion.div
                            key={n.id}
                            initial={{ opacity: 0, x: 30, scale: 0.95 }}
                            animate={{
                                opacity: 1,
                                x: 0,
                                scale: 1,
                                transition: {
                                    duration: 0.3,
                                    delay: idx * 0.05,
                                    type: "spring",
                                    stiffness: 400,
                                    damping: 25
                                }
                            }}
                            exit={{ opacity: 0, x: 30, scale: 0.95, transition: { duration: 0.3 } }}
                            layout
                        >
                            {renderers[n.type](n.id)}
                        </motion.div>
                    )
                )}
            </AnimatePresence>
        </aside>
    )
}

NotificationsBus.displayName = "NotificationsBus"

export { NotificationsBus }
